import { useEffect, useMemo, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import logo from '../../../../images/logo.png';
import { getPublicPageBySlug, resolvePublicAssetUrl } from '../../../../services/PublicPageService';
import { getImageFramingStyle } from '../../../../utils/ImageFramingUtils';
import './PublicPage.css';
import PublicBrandSignature from '../../../components/Common/PublicBrandSignature';

// Mantém a URL canônica sempre em minúsculo quando alguém digita /p/Slug.
const normalizeSlug = (value) => (value || '').trim().toLowerCase();

const PublicPage = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [page, setPage] = useState(null);
    const [currentStep, setCurrentStep] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const normalizedSlug = useMemo(() => normalizeSlug(slug), [slug]);

    useEffect(() => {
        // Slug vazio não deve chamar a API porque não identifica nenhuma empresa.
        if (!normalizedSlug) {
            setError('Link público inválido.');
            setLoading(false);
            return;
        }

        // Redireciona maiúsculas para a versão canônica em lowercase.
        if (slug !== normalizedSlug) {
            navigate(`/p/${normalizedSlug}`, { replace: true });
            return;
        }

        setLoading(true);
        getPublicPageBySlug(normalizedSlug)
            .then((response) => {
                setPage(response.data);
                setCurrentStep(0);
                setError('');
            })
            .catch(() => setError('Não encontramos esta página pública.'))
            .finally(() => setLoading(false));
    }, [slug, normalizedSlug, navigate]);

    const steps = page?.steps?.length ? page.steps : [];
    const step = steps[currentStep] || {};
    const isLastStep = currentStep >= steps.length - 1;
    const imageUrl = resolvePublicAssetUrl(step.imageUrl || page?.bannerUrl);
    const logoUrl = resolvePublicAssetUrl(page?.logoUrl);
    const initials = (page?.companyName || 'C').trim().slice(0, 1).toUpperCase();

    useEffect(() => {
        /*
         * Quando a empresa escolhe 0 cards, não existe introdução para exibir.
         * Nesse caso, o cliente segue direto para o agendamento público.
         */
        if (!loading && !error && page && steps.length === 0) {
            navigate(page.bookingUrl, { replace: true });
        }
    }, [loading, error, page, steps.length, navigate]);

    // Injeta manifest e apple-touch-icon dinâmicos para que, ao salvar o atalho
    // no smartphone a partir do link curto /p/{slug}, o ícone exibido seja a logo
    // do tenant em vez do ícone genérico. Mesma lógica usada em PublicBookingPage,
    // necessária aqui porque o cliente pode instalar o atalho já nesta tela de
    // introdução, antes de navegar para /booking.
    useEffect(() => {
        if (!page) return;

        const iconUrl = resolvePublicAssetUrl(page.logoUrl);

        // --- Manifest dinâmico (Android Chrome) ---
        const manifest = {
            name: page.companyName,
            short_name: page.companyName,
            icons: iconUrl
                ? [
                    { src: iconUrl, sizes: '192x192' },
                    { src: iconUrl, sizes: '512x512' }
                  ]
                : [],
            start_url: page.bookingUrl,
            display: 'standalone',
            theme_color: page.headerColor || '#000000',
            background_color: '#ffffff'
        };

        const blob = new Blob([JSON.stringify(manifest)], { type: 'application/manifest+json' });
        const blobUrl = URL.createObjectURL(blob);

        let manifestLink = document.querySelector('link[rel="manifest"]');
        const prevManifestHref = manifestLink ? manifestLink.getAttribute('href') : null;
        if (!manifestLink) {
            manifestLink = document.createElement('link');
            manifestLink.rel = 'manifest';
            document.head.appendChild(manifestLink);
        }
        manifestLink.setAttribute('href', blobUrl);

        // --- Apple Touch Icon (iOS — ignora mudanças no manifest via JS) ---
        let appleIconLink = document.querySelector('link[rel="apple-touch-icon"]');
        const prevAppleHref = appleIconLink ? appleIconLink.getAttribute('href') : null;
        if (iconUrl) {
            if (!appleIconLink) {
                appleIconLink = document.createElement('link');
                appleIconLink.rel = 'apple-touch-icon';
                document.head.appendChild(appleIconLink);
            }
            appleIconLink.setAttribute('href', iconUrl);
        }

        // --- Meta tags iOS: iOS Safari prioriza apple-mobile-web-app-title sobre <title>
        // ao nomear o atalho na tela inicial, então sem isso o nome ficava genérico ("Colmeia"). ---
        const metas = [
            { name: 'apple-mobile-web-app-capable', content: 'yes' },
            { name: 'apple-mobile-web-app-title', content: page.companyName },
            { name: 'theme-color', content: page.headerColor || '#000000' }
        ];
        const createdMetas = [];
        const prevMetaContent = {};
        metas.forEach(({ name, content }) => {
            let tag = document.querySelector(`meta[name="${name}"]`);
            if (tag) {
                prevMetaContent[name] = tag.getAttribute('content');
            } else {
                tag = document.createElement('meta');
                tag.setAttribute('name', name);
                document.head.appendChild(tag);
                createdMetas.push(tag);
            }
            tag.setAttribute('content', content);
        });

        // --- Título da aba ---
        const prevTitle = document.title;
        document.title = page.companyName;

        return () => {
            URL.revokeObjectURL(blobUrl);
            if (manifestLink) {
                if (prevManifestHref) manifestLink.setAttribute('href', prevManifestHref);
                else manifestLink.remove();
            }
            if (appleIconLink && prevAppleHref) appleIconLink.setAttribute('href', prevAppleHref);
            Object.entries(prevMetaContent).forEach(([name, content]) => {
                const tag = document.querySelector(`meta[name="${name}"]`);
                if (tag && content != null) tag.setAttribute('content', content);
            });
            createdMetas.forEach((tag) => tag.remove());
            document.title = prevTitle;
        };
    }, [page]);

    const handleNext = () => {
        // Enquanto houver etapas, o botão apenas avança o conteúdo da intro.
        if (!isLastStep) {
            setCurrentStep((prev) => prev + 1);
            return;
        }

        // No fim da intro, o cliente continua no fluxo público de agendamento.
        navigate(page.bookingUrl);
    };

    if (loading) {
        return (
            <div className="public-page__loading">
                <div>
                    <Spinner animation="border" variant="dark" />
                    <p className="mt-3 mb-0 text-muted">Carregando página...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="public-page__error">
                <div>
                    <img src={logo} alt="Colmeia" style={{ width: 64, marginBottom: 18 }} />
                    <h3>{error}</h3>
                    <button type="button" className="public-page__button mt-3" onClick={() => navigate('/')}>
                        Voltar
                    </button>
                </div>
            </div>
        );
    }

    if (page && steps.length === 0) {
        return null;
    }

    return (
        <div className="public-page">
            <main className="public-page__shell">
                <section className="public-page__media" aria-hidden="true">
                    {imageUrl ? (
                        <img
                            src={imageUrl}
                            alt=""
                            className="public-page__image"
                            // Foco e zoom escolhidos em Identidade visual; sem eles, a foto fica centralizada.
                            style={getImageFramingStyle({
                                positionX: step.imagePositionX,
                                positionY: step.imagePositionY,
                                zoom: step.imageZoom,
                            })}
                        />
                    ) : (
                        <>
                            {/* Fallback visual quando a empresa ainda não configurou imagem. */}
                            <div className="public-page__image-placeholder">{initials}</div>
                        </>
                    )}
                </section>

                <section className="public-page__content">
                    <div className="public-page__brand">
                        <img
                            src={logoUrl || logo}
                            alt={page.companyName || 'Colmeia'}
                            className="public-page__logo"
                            onError={(event) => { event.currentTarget.src = logo; }}
                        />
                        <div className="public-page__company">{page.companyName}</div>
                    </div>

                    <div className="public-page__step-count">
                        {currentStep + 1} de {steps.length}
                    </div>
                    <h1 className="public-page__title">{step.title}</h1>
                    <p className="public-page__text">{step.text}</p>

                    <div className="public-page__actions">
                        <button type="button" className="public-page__button" onClick={handleNext}>
                            {isLastStep ? 'Agendar agora' : 'Próximo'}
                            <span aria-hidden="true">-&gt;</span>
                        </button>
                        {currentStep > 0 && (
                            <button
                                type="button"
                                className="public-page__secondary"
                                onClick={() => setCurrentStep((prev) => Math.max(prev - 1, 0))}
                            >
                                Voltar
                            </button>
                        )}
                    </div>

                    <div className="public-page__dots" aria-hidden="true">
                        {/* Indicadores compactos das três etapas, sem virar controle extra. */}
                        {steps.map((item, index) => (
                            <span
                                key={`${item.title}-${index}`}
                                className={`public-page__dot ${index === currentStep ? 'is-active' : ''}`}
                            />
                        ))}
                    </div>
                </section>

                <PublicBrandSignature />
            </main>
        </div>
    );
};

export default PublicPage;
